import React, { createContext, useContext, useState, ReactNode } from "react";
import { Toast } from "@geotab/zenith";
import { TToastType } from "@geotab/zenith/dist/toastRaw/toastRaw";
import { IAction } from "@geotab/zenith/esm/toastRaw/toastRaw";
import constants, { SECONDS } from "../../../constants";

interface ToastContextValue {
    openToast: (state: ToastState) => void;
    showToast: (message: string, type?: TToastType, action?: IAction, showSeconds?: number) => void;
    showSuccessToast: (message: string) => void;
    showInfoToast: (message: string) => void;
    closeToast: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export interface ToastState {
    type: TToastType;
    message: string;
    action?: IAction;
    showSeconds?: number;
}

export const defaultToastState: ToastState = {
    type: "info",
    message: "",
    action: undefined
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isToastOpen, setIsToastOpen] = useState(false);
    const [toastState, setToastState] = useState<ToastState>(defaultToastState);

    const openToast = (state: ToastState) => {
        setToastState(state);
        setIsToastOpen(true);
    };

    const showToast = (message: string, type: TToastType = "info", action?: IAction, showSeconds?: number) => {
        setToastState({
            type,
            message,
            action,
            showSeconds
        });
        setIsToastOpen(true);
    };

    const showSuccessToast = (message: string) => {
        showToast(message, "success");
    };

    const showInfoToast = (message: string) => {
        showToast(message, "info");
    };

    const closeToast = () => {
        setToastState(defaultToastState);
        setIsToastOpen(false);
    };

    return (
        <ToastContext.Provider
            value={{
                openToast,
                showToast,
                showSuccessToast,
                showInfoToast,
                closeToast
            }}
        >
            <Toast
                type={toastState.type}
                isOpen={isToastOpen}
                header={toastState.message}
                onClose={closeToast}
                action={toastState.action}
                showSeconds={toastState.showSeconds ?? constants.HOS.TOAST_MESSAGE_TIMEOUT / SECONDS}
            />
            {children}
        </ToastContext.Provider>
    );
};

export const useToastContext = (): ToastContextValue => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToastContext must be used within a ToastProvider");
    }
    return context;
};